import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, getStoredUser, errMsg } from '@/lib/api';
import { useI18n } from '@/lib/i18n';

const readFile = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function Checkin() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [f, setF] = useState({
    weight: '', training_done: null, nutrition_kept: null, water_drunk: null,
    sleep_quality: 7, stress_level: 5, energy_level: 7, motivation: 8,
    problems: '', note_to_coach: '',
  });
  const [photos, setPhotos] = useState({ front: null, side: null, back: null });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.weight) return toast.error(t('checkin.weight'));
    setLoading(true);
    try {
      await api.post('/checkins', {
        weight: parseFloat(String(f.weight).replace(',', '.')),
        training_done: f.training_done, nutrition_kept: f.nutrition_kept, water_drunk: f.water_drunk,
        sleep_quality: f.sleep_quality, stress_level: f.stress_level,
        energy_level: f.energy_level, motivation: f.motivation,
        problems: f.problems, note_to_coach: f.note_to_coach,
      });
      for (const angle of ['front', 'side', 'back']) {
        if (photos[angle]) {
          const img = await readFile(photos[angle]);
          await api.post('/photos', { image: img, category: 'checkin', angle });
        }
      }
      toast.success(t('checkin.done'));
      const u = getStoredUser();
      nav(u?.role === 'admin' ? '/coach/clients' : '/app/home');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-asgym-bg">
      <div className="mx-auto w-full max-w-[480px] px-6 py-10">
        <button onClick={() => nav(-1)} className="mb-6 flex items-center gap-1.5 text-sm font-bold text-asgym-accent" data-testid="checkin-back">
          <ArrowLeft size={16} /> {t('common.back').toUpperCase()}
        </button>
        <h1 className="mb-8 font-display text-3xl font-extrabold leading-tight tracking-tight text-white">{t('checkin.title')}</h1>

        <label className="as-label mb-2 block">{t('checkin.weight')}</label>
        <input data-testid="checkin-weight" className="as-input mb-6" inputMode="decimal" placeholder={t('checkin.weightPh')} value={f.weight} onChange={(e) => set('weight', e.target.value)} />

        {[['training_done', 'checkin.trainingDone'], ['nutrition_kept', 'checkin.nutritionKept'], ['water_drunk', 'checkin.water']].map(([key, tk]) => (
          <div key={key} className="mb-4">
            <label className="as-label mb-2 block">{t(tk)}</label>
            <div className="flex gap-2">
              {[[true, t('common.yes')], [false, t('common.no')]].map(([val, lbl]) => (
                <button key={String(val)} data-testid={`${key}-${val}`} onClick={() => set(key, val)}
                  className={`flex-1 rounded-xl border py-3 text-xs font-extrabold tracking-widest transition-colors ${f[key] === val ? (val ? 'border-asgym-accent/50 bg-asgym-accent/15 text-asgym-accent' : 'border-asgym-error/50 bg-asgym-error/10 text-asgym-error') : 'border-white/[0.06] bg-asgym-surface text-asgym-sub'}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
        ))}

        {[['sleep_quality', 'checkin.sleep'], ['stress_level', 'checkin.stress'], ['energy_level', 'checkin.energy'], ['motivation', 'checkin.motivation']].map(([key, tk]) => (
          <div key={key} className="mb-5">
            <div className="mb-2 flex items-center justify-between">
              <label className="as-label">{t(tk)}</label>
              <span className="font-display text-lg font-extrabold text-asgym-accent">{f[key]}<span className="text-xs text-asgym-muted">/10</span></span>
            </div>
            <input type="range" min="1" max="10" value={f[key]} onChange={(e) => set(key, parseInt(e.target.value, 10))} data-testid={`${key}-slider`} className="w-full accent-asgym-accent" />
          </div>
        ))}

        <label className="as-label mb-2 mt-2 block">{t('checkin.problems')}</label>
        <textarea data-testid="checkin-problems" className="as-input mb-4 h-24 resize-none py-3" value={f.problems} onChange={(e) => set('problems', e.target.value)} />
        <label className="as-label mb-2 block">{t('checkin.note')}</label>
        <textarea data-testid="checkin-note" className="as-input mb-6 h-24 resize-none py-3" value={f.note_to_coach} onChange={(e) => set('note_to_coach', e.target.value)} />

        <label className="as-label mb-2 block">{t('checkin.progressPhotos')}</label>
        <p className="mb-3 text-[11px] text-asgym-muted">{t('checkin.photoHint')}</p>
        <div className="mb-8 grid grid-cols-3 gap-2">
          {[['front', 'progress.front'], ['side', 'progress.side'], ['back', 'progress.back']].map(([angle, tk]) => (
            <label key={angle} className={`flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border text-[10px] font-bold tracking-wide ${photos[angle] ? 'border-asgym-accent/50 bg-asgym-accent/10 text-asgym-accent' : 'border-dashed border-white/15 bg-white/[0.02] text-asgym-muted'}`}>
              {photos[angle] ? '✓' : '+'}<span>{t(tk)}</span>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhotos((p) => ({ ...p, [angle]: e.target.files?.[0] || null }))} />
            </label>
          ))}
        </div>

        <button data-testid="checkin-submit" onClick={submit} disabled={loading} className="as-btn-primary mb-10 w-full">
          {loading ? <Loader2 className="animate-spin" size={20} /> : t('checkin.submit')}
        </button>
      </div>
    </div>
  );
}
